/**
 * ComposioConnectCoach — the big, unmissable "this is expected" panel shown
 * while an app-connection popup is open (task #46, 2026-08-11).
 *
 * Owner report: the provider consent screen says "continue to Composio" /
 * "Composio wants access to your Google Account", and users who have never
 * heard that name read it as a phishing attempt. The earlier fix explained
 * it in small inline text — too easy to miss next to a full-screen OAuth
 * dialog.
 *
 * This is the loud version: a large branded panel pinned to the RIGHT side
 * of the viewport (where the browser opens the sign-in popup), with an arrow
 * pointing up-and-right at it, big type, and the gold palette. It appears
 * only while a connection is actually in flight, and never blocks the page.
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { CONNECT_COACH_Z_INDEX } from '../../styles/layers';
import { connectCoachLines } from './connectCoachLines';

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to   { opacity: 1; transform: translateX(0); }
`;

/* The rotation is baked into every keyframe — transform is not additive, so
   a plain translate would reset the arrow's tilt mid-animation. */
const nudge = keyframes`
  0%, 100% { transform: rotate(-18deg) translate(0, 0); }
  50%      { transform: rotate(-18deg) translate(-10px, -3px); }
`;

/* Right-anchored so it sits beside the popup the browser opens (top-right on
   every major desktop browser). On narrow screens it becomes a bottom sheet —
   the popup takes the whole screen there anyway. */
const Panel = styled.aside`
  position: fixed;
  /* Above the shared modal rung — a connect started from inside the catalog
     modal must show the coach ON TOP of it. */
  z-index: ${CONNECT_COACH_Z_INDEX};
  right: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: min(420px, calc(100vw - 3rem));
  background: #ffffff;
  border: 2px solid var(--brand-gold);
  border-radius: 1.1rem;
  box-shadow: 0 1.5rem 3rem rgba(0, 0, 0, 0.18);
  padding: 1.6rem 1.5rem 1.4rem;
  animation: ${slideIn} 0.25s ease-out;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 900px) {
    right: 1rem;
    left: 1rem;
    top: auto;
    bottom: 1rem;
    transform: none;
    width: auto;
  }
`;

/* Large arrow pointing LEFT with a slight upward tilt, toward the sign-in
   popup (which connectAppInFlow pins to the top-LEFT of the screen, so the
   relationship is deterministic). Task #53 owner feedback: the old diagonal
   glyph read as "look straight up" — the popup is mostly to the LEFT of
   this panel, only a little above it. */
const Arrow = styled.div`
  position: absolute;
  top: 1.1rem;
  left: -3.4rem;
  font-size: 2.6rem;
  line-height: 1;
  color: var(--brand-gold);
  animation: ${nudge} 1.6s ease-in-out infinite;
  user-select: none;

  @media (max-width: 900px) {
    display: none;
  }
`;

const Eyebrow = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: lowercase;
  color: var(--brand-gold-hover);
  margin-bottom: 0.5rem;
`;

const Headline = styled.h3`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  /* Deliberately large — this must win against a full OAuth dialog. */
  font-size: 1.45rem;
  line-height: 1.25;
  font-weight: 600;
  color: var(--brand-text);
  margin: 0 0 0.7rem;
  text-wrap: balance;
`;

const Body = styled.p`
  font-size: 1rem;
  line-height: 1.55;
  color: #444444;
  margin: 0 0 0.9rem;
  text-wrap: pretty;
`;

const Reassure = styled.ul`
  list-style: none;
  margin: 0 0 1rem;
  padding: 0;
  display: grid;
  gap: 0.45rem;
  font-size: 0.95rem;
  color: #333333;
  line-height: 1.45;

  li {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
  }
`;

const Check = styled.span`
  color: #1B7A3D;
  font-weight: 700;
  flex-shrink: 0;
`;

const Dismiss = styled.button`
  background: none;
  border: none;
  color: var(--brand-text-muted);
  font-family: inherit;
  font-size: 0.85rem;
  text-transform: lowercase;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;

  &:hover { color: var(--brand-text); }
`;

export interface ComposioConnectCoachProps {
  /** Human label of the app being connected (e.g. "gmail"). */
  appLabel?: string;
  /** Email journey (2026-08-20): the inbox the user said they are
   *  connecting — the coach pins the sign-in to it. */
  intendedAddress?: string;
  /** Toolkit slug — Microsoft gets the admin-approval warning. */
  toolkitSlug?: string;
  onDismiss: () => void;
}

export const ComposioConnectCoach: React.FC<ComposioConnectCoachProps> = ({
  appLabel,
  intendedAddress,
  toolkitSlug,
  onDismiss,
}) => {
  // Which of its lines it says: one choice, which the new design's coach asks too (connectCoachLines.ts).
  const lines = connectCoachLines(intendedAddress, toolkitSlug);
  return (
    <Panel role="status" aria-live="polite" data-testid="composio-connect-coach">
      <Arrow aria-hidden="true">←</Arrow>
      <Eyebrow>this is expected</Eyebrow>
      <Headline>
        the sign-in window will say <strong>&ldquo;Composio&rdquo;</strong>
      </Headline>
      <Body>
        Composio is Prest0n&rsquo;s secure connection partner — it&rsquo;s how
        Prest0n gets permission to work inside
        {appLabel ? ` your ${appLabel}` : ' your apps'}.
      </Body>
      {lines.intendedAddress && (
        <Body data-testid="coach-intended-address">
          sign in as <strong>{lines.intendedAddress}</strong> — if a different account
          appears, pick &ldquo;use another account&rdquo;.
        </Body>
      )}
      {lines.googleChoose && (
        // Google signs in with the account already open in this browser unless asked (2026-10-10, Miles's
        // "it keeps defaulting to Justin's Google account").
        <Body data-testid="coach-google-choose">
          google may pick the account already signed in on this browser — choose
          the one you mean, or pick &ldquo;use another account&rdquo;.
        </Body>
      )}
      {lines.adminWarning && (
        <Body data-testid="coach-admin-warning">
          if microsoft asks for approval: the admin approves it from their admin
          console — never by signing in themselves. you sign in as the inbox.
        </Body>
      )}
      <Reassure>
        <li><Check>✓</Check><span>you sign in with the provider directly — never with Prest0n</span></li>
        <li><Check>✓</Check><span>your password is never shared with Prest0n or Composio</span></li>
        <li><Check>✓</Check><span>you stay in control — choose what Prest0n can do, change it anytime</span></li>
        <li><Check>✓</Check><span>disconnect any app later from the integrations page</span></li>
      </Reassure>
      <Dismiss type="button" onClick={onDismiss}>got it</Dismiss>
    </Panel>
  );
};

export default ComposioConnectCoach;
